// EXTERNAL DEPENDENCIES
import React, { useEffect, useState } from 'react'
import { useIsMutating } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'

// COMPONENTS
import { Link, Row } from '@paypalcorp/pp-react'
import ContactInfoSection from '@/common/components/ui/contact-info-section'
import { WithLoadingShimmer } from '@/common/components/loading/with-loading-shimmer'
import {
  BusinessDescriptionSection,
  BusinessNameSection,
  BusinessUsernameSection,
  EditBusinessLocationNameSection,
} from '../../../'
import { NavigationButtons } from '../navigation-buttons'
import { ProfilePhotosSection } from './components'

// HELPERS
import {
  useBusinessProfilePageContent,
  useBusinessProfileReduxState,
} from '../../../../hooks'

// REDUX
import {
  updateEditedProfileDataValues,
  updateIsEditing,
  updateAreValuesEdited,
} from '@/redux/features'

// CONSTANTS
import { routes } from '@/api/constants'
import { EDIT_PROFILE_PANEL_TEST_ID } from '@/screens/business-profile-page/constants/test-ids'

// FPTI
import { EDIT_PROFILE_PANEL_NEXT_BUTTON_FPTI } from './fpti'
import { MODULAR_EDIT_PROFILE_PANEL_EDIT_BUSINESS_INFO_FPTI } from '@/screens/business-profile-page/fpti'

// STYLES
import styles from './styles.module.less'

/**
 * Shows all of the editable/non-editable business information fields
 */
export function EditProfilePanel({
  isLoading,
  resetEditedData,
}: {
  isLoading: boolean
  resetEditedData: () => void
}) {
  const validationInProgress = useIsMutating() > 0
  const dispatch = useDispatch()
  const getContent = useBusinessProfilePageContent()

  // STATE START
  const {
    editedProfileData: { values },
  } = useBusinessProfileReduxState()

  // STATE END
  const [isFormValid, setIsFormValid] = useState(true)
  const [isContactInfoValid, setIsContactInfoValid] = useState(true)
  const [isBusinessDescriptionValid, setIsBusinessDescriptionValid] =
    useState(true)
  const [isProfilePhotoValid, setIsProfilePhotoValid] = useState(true)
  const [isCoverPhotoValid, setIsCoverPhotoValid] = useState(true)

  useEffect(() => {
    setIsFormValid(
      !validationInProgress &&
        isContactInfoValid &&
        isBusinessDescriptionValid &&
        isProfilePhotoValid &&
        isCoverPhotoValid,
    )
  }, [
    validationInProgress,
    isBusinessDescriptionValid,
    isContactInfoValid,
    isCoverPhotoValid,
    isProfilePhotoValid,
  ])

  return (
    <div
      className={styles.editProfilePanelContainer}
      data-testid={EDIT_PROFILE_PANEL_TEST_ID}
    >
      <ProfilePhotosSection
        setIsCoverPhotoValid={setIsCoverPhotoValid}
        setIsProfilePhotoValid={setIsProfilePhotoValid}
      />
      <BusinessNameSection />
      <BusinessUsernameSection />
      <BusinessDescriptionSection
        isLoading={isLoading}
        setIsValid={setIsBusinessDescriptionValid}
      />
      <Row noGutters className={styles.sectionContainer}>
        <EditBusinessLocationNameSection />
      </Row>
      <Row noGutters className={styles.sectionContainer}>
        <ContactInfoSection
          isValid={isContactInfoValid}
          setIsValid={setIsContactInfoValid}
          updateSelectedContactInfo={(updatedContactInfo: object) => {
            dispatch(
              updateEditedProfileDataValues({
                contactInfo: updatedContactInfo,
              }),
            )
            dispatch(updateAreValuesEdited(true))
          }}
          allContactInfo={values?.allContactInfo}
          selectedContactInfoValues={values?.contactInfo}
        />
      </Row>
      <WithLoadingShimmer isLoading={isLoading}>
        <NavigationButtons
          isFormValid={isFormValid}
          pageIsValidated={!validationInProgress || isFormValid}
          nextButtonProps={{ fpti: EDIT_PROFILE_PANEL_NEXT_BUTTON_FPTI }}
          backButtonProps={{
            handleClick: () => {
              dispatch(updateIsEditing(false))
              resetEditedData()
            },
          }}
          optionalLink={
            <Link
              href={routes.EDIT_BUSINESS_PROFILE_SETTINGS}
              target='_blank'
              data-fpti={MODULAR_EDIT_PROFILE_PANEL_EDIT_BUSINESS_INFO_FPTI}
            >
              {getContent('editBusinessInfoLink')}
            </Link>
          }
        />
      </WithLoadingShimmer>
    </div>
  )
}
